import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPayrocHostedFieldSession } from '../../services/paymentService';
import { Row, Col, Spinner } from 'react-bootstrap';
import { payrocCss } from './payrocCss';
import { warningSwal } from '../../utils/helper';
import './index.scss';

const PayrocCreditCardPaymentForm = React.memo(
    ({ handleSubmit, setCreditCardSessionTimer, tripSetting = null, companyID }) => {
        const sessionExpiresAtRef = useRef(null);
        const sessionTimeoutRef = useRef(null);
        const scriptRef = useRef(null);
        const cardFormRef = useRef(null);
        const isMountedRef = useRef(true);
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            if (sessionExpiresAtRef.current) {
                /* 
                At the moment, the Payroc expiresAt token is incorrectly set to 1 hour and 10 minutes instead of 10 minutes. 
                We eventually want to set this timer based on the token's expiredAt value once that is resolved.
                For now, we are manually setting the timer to 10 minutes (600 seconds) to match the Payroc token expiration time.
            */
                setCreditCardSessionTimer(600);
                sessionTimeoutRef.current = setTimeout(async () => {
                    const endedSession = await warningSwal({
                        title: 'Session Expired',
                        text: 'The current credit card payment session has expired and needs to be refreshed.',
                    });

                    if (endedSession.isConfirmed) {
                        window.location.reload();
                    }
                }, 600000);
            }

            return () => {
                if (sessionTimeoutRef.current) {
                    clearTimeout(sessionTimeoutRef.current);
                }
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [sessionExpiresAtRef?.current]);

        // Otherwise, we see a handful of dev errors.
        useEffect(() => {
            isMountedRef.current = true;

            const initPayrocFields = () => {
                const waitForFormFields = () => {
                    // Check if component is still mounted
                    if (!isMountedRef.current) {
                        return;
                    }

                    const allExist = [
                        '.card-holder-name',
                        '.card-number',
                        '.card-cvv',
                        '.card-expiry',
                        '.submit-button',
                    ].every((sel) => {
                        const exists = document.querySelector(sel);
                        return exists;
                    });

                    if (allExist) {
                        // Append the script only after all form fields are rendered
                        (async () => {
                            try {
                                // Set initialization flag
                                setLoading(true);

                                const tokenRequest = await createPayrocHostedFieldSession(companyID);
                                const sessionTokenData = tokenRequest?.data;

                                // Check if component is still mounted before proceeding
                                if (!isMountedRef.current) {
                                    setLoading(false);
                                    return;
                                }

                                if (sessionTokenData) {
                                    // Check if script already exists and remove it
                                    const existingScript = document.querySelector(`script[src*="hosted-fields"]`);
                                    if (existingScript && existingScript.parentNode) {
                                        existingScript.parentNode.removeChild(existingScript);
                                    }

                                    const script = document.createElement('script');
                                    script.src = sessionTokenData.isDev
                                        ? 'https://cdn.uat.payroc.com/js/hosted-fields/hosted-fields-1.6.0.172429.js'
                                        : 'https://cdn.payroc.com/js/hosted-fields/hosted-fields-1.6.0.172441.js';
                                    script.integrity = sessionTokenData.isDev
                                        ? 'sha384-1DFjPXzSRRPT56FXz1ptVP4pwzXPxeNQynxQpU7+5v++p+EbWGprq5TZl8qizGKs'
                                        : 'sha384-e1tK2exEUnMhHQYhH2erv/sY3w8jzY/XnnBevNbv20VtjuJpcwb0u9h8qblYjrBl';
                                    script.crossOrigin = 'anonymous';
                                    script.async = true;
                                    const expiresAt = new Date(sessionTokenData.expiresAt);
                                    sessionExpiresAtRef.current = expiresAt;
                                    scriptRef.current = script;

                                    script.onload = () => {
                                        // Check if component is still mounted before initializing
                                        if (!isMountedRef.current || !window.Payroc || !sessionTokenData) {
                                            return;
                                        }

                                        try {
                                            const cardForm = new window.Payroc.hostedFields({
                                                sessionToken: sessionTokenData.token,
                                                mode: 'payment',
                                                fields: {
                                                    card: {
                                                        cardholderName: {
                                                            target: '.card-holder-name',
                                                            errorTarget: '.card-holder-name-error',
                                                            placeholder: 'Cardholder Name',
                                                        },
                                                        cardNumber: {
                                                            target: '.card-number',
                                                            errorTarget: '.card-number-error',
                                                            placeholder: '1234 5678 1234 1211',
                                                        },
                                                        cvv: {
                                                            wrapperTarget: '.card-cvv-wrapper',
                                                            target: '.card-cvv',
                                                            errorTarget: '.card-cvv-error',
                                                            placeholder: 'CVV',
                                                        },
                                                        expiryDate: {
                                                            target: '.card-expiry',
                                                            errorTarget: '.card-expiry-error',
                                                            placeholder: 'MM/YY',
                                                        },
                                                        submit: {
                                                            target: '.submit-button',
                                                            value: 'ACCEPT & PAY',
                                                        },
                                                    },
                                                },
                                                styles: {
                                                    disableDefaultStyles: true,
                                                    css: {
                                                        ...payrocCss,
                                                        button: {
                                                            ...payrocCss.button,
                                                            'background-color': tripSetting
                                                                ? tripSetting.buttonColor
                                                                : '#13c513',
                                                            border: `1px ${tripSetting ? tripSetting.buttonColor : '#13c513'} solid`,
                                                        },
                                                    },
                                                },
                                            });

                                            cardFormRef.current = cardForm;

                                            cardForm.on('submissionSuccess', ({ token, expiresAt }) => {
                                                if (isMountedRef.current) {
                                                    handleSubmit(null, token, expiresAt);
                                                }
                                            });

                                            cardForm.on('error', ({ type, field, message }) => {
                                                if (isMountedRef.current) {
                                                    console.log('Payroc error :', { type, field, message });
                                                    warningSwal({
                                                        title: 'Payment Error',
                                                        html: `There was an error with the payment form:<br/><br/>
                                                       <strong>Type:</strong> ${type}<br/>
                                                       <strong>Field:</strong> ${field}<br/>
                                                       <strong>Message:</strong> ${message}`,
                                                    });
                                                }
                                            });

                                            if (isMountedRef.current) {
                                                cardForm.initialize();
                                            }
                                        } catch (e) {
                                            if (isMountedRef.current) {
                                                console.error('Payroc init error:', e);
                                            }
                                        }
                                    };

                                    script.onerror = () => {
                                        if (isMountedRef.current) {
                                            console.error('Failed to load Payroc script');
                                            setLoading(false);
                                        }
                                    };

                                    document.body.appendChild(script);
                                    setLoading(false);
                                } else {
                                    if (isMountedRef.current) {
                                        console.log('Failed to create Payroc hosted field session');
                                        setLoading(false);
                                    }
                                }
                            } catch (error) {
                                if (isMountedRef.current) {
                                    console.log('Error creating Payroc session:', error);
                                    setLoading(false);
                                }
                            } finally {
                                setLoading(false);
                            }
                        })();
                    } else {
                        requestAnimationFrame(waitForFormFields);
                    }
                };

                waitForFormFields();
            };

            initPayrocFields();

            console.log('inited');

            return () => {
                // Mark component as unmounted
                isMountedRef.current = false;

                if (cardFormRef.current) {
                    try {
                        // Remove all event listeners from the eventEmitter
                        if (cardFormRef.current.eventEmitter && cardFormRef.current.eventEmitter.listeners) {
                            const listeners = cardFormRef.current.eventEmitter.listeners;
                            Object.keys(listeners).forEach((eventName) => {
                                if (Array.isArray(listeners[eventName])) {
                                    listeners[eventName].length = 0; // Clear the array
                                }
                            });
                        }

                        // Clean up iframes from the iframes array
                        // First, null out iframe references to prevent Payroc from trying to access them
                        if (Array.isArray(cardFormRef.current.iframes)) {
                            cardFormRef.current.iframes.forEach((iframeObj) => {
                                try {
                                    // Try to find and null out the iframe reference
                                    // Check common property names where the iframe element might be stored
                                    const possibleProps = ['iframe', 'element', 'iframeElement', 'node', 'el', 'frame'];
                                    possibleProps.forEach((prop) => {
                                        if (iframeObj[prop]) {
                                            // Null out the reference first to prevent errors
                                            iframeObj[prop] = null;
                                        }
                                    });
                                } catch (e) {
                                    // Ignore errors
                                }
                            });
                            // Clear the array after nulling references
                            cardFormRef.current.iframes.length = 0;
                        }

                        // Clear field embed timeouts
                        if (cardFormRef.current.fieldEmbedTimeouts && cardFormRef.current.fieldEmbedTimeouts.clear) {
                            cardFormRef.current.fieldEmbedTimeouts.clear();
                        }

                        // Clear submitted fields
                        if (Array.isArray(cardFormRef.current.submittedFields)) {
                            cardFormRef.current.submittedFields.length = 0;
                        }

                        // Clear submitting fields
                        if (Array.isArray(cardFormRef.current.submittingFields)) {
                            cardFormRef.current.submittingFields.length = 0;
                        }

                        // Try to null out postMessage methods to prevent errors
                        // These methods might try to access iframes that no longer exist
                        const postMessageMethods = [
                            'postMessageToGatewayPage',
                            'onMessageFromGatewayPage',
                            'sendMessage',
                        ];
                        postMessageMethods.forEach((methodName) => {
                            try {
                                if (typeof cardFormRef.current[methodName] === 'function') {
                                    cardFormRef.current[methodName] = () => {}; // Replace with no-op function
                                }
                            } catch (e) {
                                // Ignore errors
                            }
                        });
                    } catch (e) {
                        console.error('Error cleaning up Payroc instance:', e);
                    }
                    cardFormRef.current = null;
                }

                // Remove script tag from DOM
                if (scriptRef.current && scriptRef.current.parentNode) {
                    scriptRef.current.parentNode.removeChild(scriptRef.current);
                    scriptRef.current = null;
                }

                // Clean up form field containers - remove any iframes Payroc injected
                const formFieldContainers = [
                    '.card-holder-name',
                    '.card-number',
                    '.card-cvv',
                    '.card-expiry',
                    '.submit-button',
                ];
                formFieldContainers.forEach((selector) => {
                    const container = document.querySelector(selector);
                    if (container) {
                        // Remove all iframes from the container
                        const iframes = container.querySelectorAll('iframe');
                        iframes.forEach((iframe) => {
                            try {
                                // Try to remove the iframe
                                if (iframe.parentNode) {
                                    iframe.parentNode.removeChild(iframe);
                                }
                            } catch (e) {
                                // If removal fails, try to clear the src to stop communication
                                try {
                                    iframe.src = 'about:blank';
                                    if (iframe.contentWindow) {
                                        iframe.contentWindow.location = 'about:blank';
                                    }
                                } catch (innerError) {
                                    // Ignore errors
                                }
                            }
                        });
                        // Clear the container content to ensure clean state
                        container.innerHTML = '';
                    }
                });

                // Clear session timeout
                if (sessionTimeoutRef.current) {
                    clearTimeout(sessionTimeoutRef.current);
                }
                // Reset session expires at
                sessionExpiresAtRef.current = null;
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        return loading ? (
            <Spinner animation="border" role="status" style={{ position: 'absolute', top: '50%', left: '50%' }} />
        ) : (
            <div className="card-container payroc-form" style={{ padding: '0 15px', maxWidth: '500px' }}>
                <Row className="my-1">
                    <Col>
                        <label htmlFor="card-holder-name">Cardholder Name</label>
                        <div className="card-holder-name"></div>
                        <div className="card-holder-name-error error-message"></div>
                    </Col>
                </Row>

                <Row className="my-1">
                    <Col>
                        <label htmlFor="card-number">Card Number</label>
                        <div className="card-number"></div>
                        <div className="card-number-error error-message"></div>
                    </Col>
                </Row>

                <Row className="my-1">
                    <Col md={6}>
                        <label htmlFor="card-expiry">Expires (MM/YY)</label>
                        <div className="card-expiry"></div>
                        <div className="card-expiry-error error-message"></div>
                    </Col>
                    <Col md={6}>
                        <label htmlFor="card-cvv">CVV</label>
                        <div className="card-cvv-wrapper">
                            <div className="card-cvv"></div>
                            <div className="card-cvv-error error-message"></div>
                        </div>
                    </Col>
                </Row>

                <Row className="mt-3">
                    <Col md={12}>
                        <div className="card-submit submit-button"></div>
                    </Col>
                </Row>
            </div>
        );
    }
);

export default PayrocCreditCardPaymentForm;
